import { uploadEncryptedFile, downloadEncryptedFile, ensureFirebaseAuth } from './firebaseStorage';
import { generateThumbnail } from './thumbnailService';
import { v4 as uuidv4 } from 'uuid';

// AES-GCM encryption using Web Crypto API (replaces missing XMTP encrypt/decrypt helpers)

async function encryptPayload(data: Uint8Array): Promise<{
  payload: Uint8Array;
  salt: Uint8Array;
  nonce: Uint8Array;
  secret: Uint8Array;
  digest: Uint8Array;
}> {
  const secret = crypto.getRandomValues(new Uint8Array(32));
  const nonce = crypto.getRandomValues(new Uint8Array(12));
  const salt = crypto.getRandomValues(new Uint8Array(16));

  const key = await crypto.subtle.importKey('raw', secret, 'AES-GCM', false, ['encrypt']);
  const encrypted = await crypto.subtle.encrypt({ name: 'AES-GCM', iv: nonce }, key, data);
  const payload = new Uint8Array(encrypted);

  const digestBuf = await crypto.subtle.digest('SHA-256', payload);
  const digest = new Uint8Array(digestBuf);

  return { payload, salt, nonce, secret, digest };
}

async function decryptPayload(
  payload: Uint8Array,
  secret: Uint8Array,
  nonce: Uint8Array
): Promise<Uint8Array> {
  const key = await crypto.subtle.importKey('raw', secret, 'AES-GCM', false, ['decrypt']);
  const decrypted = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: nonce }, key, payload);
  return new Uint8Array(decrypted);
}

function toBase64(buf: Uint8Array): string {
  const CHUNK_SIZE = 8192;
  const chunks: string[] = [];
  for (let i = 0; i < buf.length; i += CHUNK_SIZE) {
    const slice = buf.subarray(i, i + CHUNK_SIZE);
    chunks.push(String.fromCharCode.apply(null, Array.from(slice)));
  }
  return btoa(chunks.join(''));
}

function fromBase64(b64: string): Uint8Array {
  const binary = atob(b64);
  const buf = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) buf[i] = binary.charCodeAt(i);
  return buf;
}

export interface RemoteAttachmentMetadata {
  url: string;
  digest: string;
  nonce: string;
  salt: string;
  secret: string;
  filename: string;
  mimeType: string;
  fileSize: number;
  thumbnailUrl?: string;
  isVoiceNote?: boolean;
  durationSecs?: number;
}

/**
 * Encrypt a file and upload to Firebase Storage.
 * For non-custodial users, authenticates with Firebase first via wallet signature.
 * @param file - The file to encrypt and upload
 * @param walletAddress - The user's EVM address (needed for non-custodial Firebase auth)
 */
export async function encryptAndUploadAttachment(
  file: File,
  walletAddress?: string
): Promise<RemoteAttachmentMetadata> {
  // Ensure Firebase Auth before uploading (no-op for custodial users who already have auth)
  await ensureFirebaseAuth(walletAddress);

  const arrayBuffer = await file.arrayBuffer();
  const fileData = new Uint8Array(arrayBuffer);

  const encrypted = await encryptPayload(fileData);

  const fileId = uuidv4();
  const storagePath = `chat-attachments/${fileId}.enc`;

  const downloadUrl = await uploadEncryptedFile(storagePath, encrypted.payload);

  let thumbnailUrl: string | undefined;
  if (file.type.startsWith('image/')) {
    try {
      const thumbnailBlob = await generateThumbnail(file);
      if (thumbnailBlob) {
        const thumbData = new Uint8Array(await thumbnailBlob.arrayBuffer());
        const encryptedThumb = await encryptPayload(thumbData);
        const thumbPath = `chat-attachments/${fileId}_thumb.enc`;
        thumbnailUrl = await uploadEncryptedFile(thumbPath, encryptedThumb.payload);
      }
    } catch (err) {
      console.warn('Thumbnail generation failed:', err);
    }
  }

  return {
    url: downloadUrl,
    digest: toBase64(encrypted.digest),
    nonce: toBase64(encrypted.nonce),
    salt: toBase64(encrypted.salt),
    secret: toBase64(encrypted.secret),
    filename: file.name,
    mimeType: file.type,
    fileSize: file.size,
    thumbnailUrl,
  };
}

/**
 * Download and decrypt an attachment from a received XMTP message.
 */
export async function downloadAndDecryptAttachment(
  content: any
): Promise<{ filename: string; mimeType: string; data: Uint8Array }> {
  const url = content.url;
  if (!url) throw new Error('No attachment URL');

  const encryptedData = await downloadEncryptedFile(url);

  const secret =
    content.secret instanceof Uint8Array ? content.secret : fromBase64(content.secret);
  const nonce =
    content.nonce instanceof Uint8Array ? content.nonce : fromBase64(content.nonce);

  const decryptedData = await decryptPayload(encryptedData, secret, nonce);

  return {
    filename: content.filename || 'file',
    mimeType: content.mimeType || 'application/octet-stream',
    data: decryptedData,
  };
}
